import type { RouteRecordRaw, RouteLocationNormalized, RouteRecordNormalized } from 'vue-router'
import type { Menu } from '@/api/common/model/userModel'
import { Layout } from '@/router/basic'
import cloneDeep from '@/utils/cloneDeep'
import { isString } from '@/utils/is'

const IFrame = () => import('@/views/iframe/FrameBlank.vue')
const ExceptionComponent = () => import('../../views/exception/Exception.vue')
const LayoutMap = new Map<string, () => Promise<typeof import('*.vue')>>()

LayoutMap.set('LAYOUT', Layout)
LayoutMap.set('FRAME', IFrame)

let dynamicViewsModules: Record<string, () => Promise<Recordable>>

function asyncImportRoute(routes: Menu[] | undefined) {
  dynamicViewsModules = dynamicViewsModules || import.meta.glob('../../views/**/*.{vue,tsx}')
  if (!routes) {
    return
  }
  routes.forEach((item) => {
    if (!item.component && item.meta?.frameSrc) {
      item.component = 'FRAME'
    }
    const { component } = item
    const { childrenPermissionList } = item
    if (isString(component) && component) {
      const layoutFound = LayoutMap.get(component.toUpperCase())
      if (layoutFound) {
        item.component = layoutFound
      } else {
        item.component = dynamicImport(dynamicViewsModules, component)
      }
    }
    childrenPermissionList && asyncImportRoute(childrenPermissionList)
  })
}

function dynamicImport(
  dynamicViewsModules: Record<string, () => Promise<Recordable>>,
  component: string,
) {
  const keys = Object.keys(dynamicViewsModules)
  const matchKeys = keys.filter((key) => {
    const k = key.replace('../../views', '')
    const startFlag = component.startsWith('/')
    const endFlag = component.endsWith('.vue') || component.endsWith('.tsx')
    const startIndex = startFlag ? 0 : 1
    const lastIndex = endFlag ? k.length : k.lastIndexOf('.')
    return k.substring(startIndex, lastIndex) === component
  })
  if (matchKeys?.length === 1) {
    const matchKey = matchKeys[0]
    return dynamicViewsModules[matchKey]
  } else if (matchKeys?.length > 1) {
    console.log('同一目录下不能重复定义vue或ts文件')
    return
  } else {
    console.error(
      '在src/views/下找不到`' + component + '.vue` 或 `' + component + '.tsx`, 请自行创建!',
    )
    return ExceptionComponent
  }
}

export function transformObjToRoute<T = RouteRecordRaw>(routeList: Menu[]): T[] {
  routeList.forEach((route) => {
    const component = route.component
    if (component) {
      if (isString(component)) {
        if (component.toUpperCase() === 'LAYOUT') {
          route.component = LayoutMap.get(component.toUpperCase())
        } else {
          route.childrenPermissionList = [cloneDeep(route)]
          route.component = Layout
          route.name = `${route.name}Parent`
          route.path = ''
          const meta = route.meta || {}
          meta.affix = false
          route.meta = meta
        }
      }
    } else {
      console.log('请正确配置路由：' + route?.name + '的component属性')
    }
    route.childrenPermissionList && asyncImportRoute(route.childrenPermissionList)
  })
  return routeList as unknown as T[]
}

export function transformMenuToRoute<T = RouteRecordRaw>(routeList: Menu[]): T[] {
  routeList.forEach((route) => {
    const component = route.component
    route.path = route.url
    if (route.meta && route.meta.frameSrc) {
      route.meta.frameSrc = /^https?:\/\//.test(route.meta.frameSrc)
        ? route.meta.frameSrc
        : (import.meta.env.DEV ? 'http://dev-wms-web.eminxing.com' : '') + route.meta.frameSrc
    }
    if (component) {
      if (isString(component)) {
        const componentName = component.toUpperCase()
        if (componentName === 'LAYOUT' || componentName === 'FRAME') {
          route.component = LayoutMap.get(componentName)
        }
      }
    } else {
      console.log('请正确配置路由：' + route?.name + '的component属性')
    }
  })
  return routeList as unknown as T[]
}

/**
 * 获取路由的name属性
 * @param routes
 * @returns
 */
export const getRouteName = (routes: RouteRecordRaw[]) => {
  let routeNames: string[] = []
  routes.forEach((route) => {
    const { name } = route
    name && routeNames.push(name as string)
    if (route.children) {
      routeNames = routeNames.concat(getRouteName(route.children))
    }
  })
  return routeNames
}

/**
 * 将route对象的matched属性只保留meta, name, path三个属性
 * @param route
 * @returns
 */
export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) {
    return route
  }
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path,
        }))
      : undefined) as RouteRecordNormalized[],
  }
}
